import React, { useContext } from 'react';
import { FormattedCurrency } from '@qover/react-intl';
import { PrimaryButton } from '@qover/molecules';
import { EmailModalTrigger } from '../modals';
import { useMedia } from '@qover/hooks';
// @ts-ignore
import { ThemeContext } from '@qover/styles';
import { GhostBadge } from '@qover/atoms';
import { roundUpCurrency } from '@qover/utils/src/tools';
import {
  ActionContainer,
  Column,
  Container,
  EmailModalButtonContainer,
  InnerContainer,
  Option,
  OptionContainer,
  PriceContainer,
  RightContainer,
  Row,
  PriceRow,
  Price,
} from './cartSection.styles';
import { Caption, Footnote, Sitemap } from '@qover/typography';
import { canUseDOM } from '@qover/utils/src/dom';

type Option = {
  icon: React.ReactNode;
  label: React.ReactNode;
};

export enum CartPaymentFrequencyPosition {
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
  RIGHT = 'RIGHT',
}

type CartSectionProps = {
  badge?: JSX.Element;
  cartTitle: React.ReactNode;
  className?: string;
  buttonTitle: React.ReactNode;
  disabled?: boolean;
  email?: string;
  onEmailModalSubmit?: (form: any) => Promise<unknown>;
  options?: Option[];
  price: number;
  paymentFrequencyPosition?: CartPaymentFrequencyPosition;
  paymentFrequency?: React.ReactNode;
  secondaryPrice?: number;
  secondaryPaymentFrequency?: React.ReactNode;
  showEmailModalButton?: boolean;
};

type PriceProps = {
  badge?: JSX.Element;
  'data-test': string;
  paymentFrequencyPosition: CartPaymentFrequencyPosition;
  paymentFrequency?: React.ReactNode;
  secondaryPrice?: number;
  secondaryPaymentFrequency?: React.ReactNode;
  price: number;
};

const PriceComponent: React.FC<PriceProps> = props => (
  <PriceRow>
    <PriceContainer>
      {props.paymentFrequencyPosition === CartPaymentFrequencyPosition.TOP && (
        <Sitemap>{props.paymentFrequency}</Sitemap>
      )}
      <Price data-test={props['data-test']}>
        <FormattedCurrency value={roundUpCurrency(props.price)} />
        {props.paymentFrequencyPosition ===
          CartPaymentFrequencyPosition.RIGHT && (
          <Sitemap>&nbsp;{props.paymentFrequency}</Sitemap>
        )}
      </Price>
      {props.paymentFrequencyPosition ===
        CartPaymentFrequencyPosition.BOTTOM && (
        <Sitemap>{props.paymentFrequency}</Sitemap>
      )}
      {!!props.secondaryPrice && props.secondaryPaymentFrequency && (
        <Footnote style={{ marginTop: '5px' }}>
          <FormattedCurrency value={roundUpCurrency(props.secondaryPrice)} />
          &nbsp;
          {props.secondaryPaymentFrequency}
        </Footnote>
      )}
    </PriceContainer>
    {props.badge && (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
        }}
      >
        <GhostBadge className="rounded">{props.badge}</GhostBadge>
      </div>
    )}
  </PriceRow>
);

type EmailButtonProps = {
  className: string;
  email?: string;
  onEmailModalSubmit?: (form: any) => Promise<unknown>;
  showEmailModalButton?: boolean;
};

const EmailButton: React.FC<EmailButtonProps> = props => (
  <EmailModalButtonContainer className={props.className}>
    {props.showEmailModalButton && props.onEmailModalSubmit && (
      <EmailModalTrigger
        email={props.email}
        onSubmit={props.onEmailModalSubmit}
      />
    )}
  </EmailModalButtonContainer>
);

export const CartSection: React.FC<CartSectionProps> = props => {
  const theme = useContext<any>(ThemeContext);
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);

  const {
    paymentFrequencyPosition = CartPaymentFrequencyPosition.BOTTOM,
  } = props;

  return (
    <Container className={props.className}>
      <InnerContainer>
        <OptionContainer>
          {!!props.options?.length && (
            <>
              <Column>
                <Caption className="upper font-bold text-left">
                  {props.cartTitle}
                </Caption>
                <Row>
                  {props.options.map((option, idx) => (
                    <Option key={idx}>
                      {option.icon}
                      <Footnote>{option.label}</Footnote>
                    </Option>
                  ))}
                </Row>
              </Column>
            </>
          )}
          {isMobile && canUseDOM() && (
            <PriceComponent
              badge={props.badge}
              key="general.mobile.bottomNav.price"
              data-test="general.mobile.bottomNav.price"
              paymentFrequency={props.paymentFrequency}
              paymentFrequencyPosition={paymentFrequencyPosition}
              price={props.price}
              secondaryPaymentFrequency={props.secondaryPaymentFrequency}
              secondaryPrice={props.secondaryPrice}
            />
          )}
        </OptionContainer>
        <RightContainer>
          <ActionContainer>
            {!isMobile && canUseDOM() && (
              <PriceComponent
                badge={props.badge}
                key="general.desktop.bottomNav.price"
                data-test="general.desktop.bottomNav.price"
                paymentFrequency={props.paymentFrequency}
                paymentFrequencyPosition={paymentFrequencyPosition}
                price={props.price}
                secondaryPaymentFrequency={props.secondaryPaymentFrequency}
                secondaryPrice={props.secondaryPrice}
              />
            )}
            <PrimaryButton
              data-test="bottomNav.continue"
              disabled={props.disabled}
            >
              {props.buttonTitle}
            </PrimaryButton>
          </ActionContainer>
          {theme.featureToggles?.showSendMyOffer && (
            <EmailButton
              className="hide-for-small"
              email={props.email}
              onEmailModalSubmit={props.onEmailModalSubmit}
              showEmailModalButton={props.showEmailModalButton}
            />
          )}
        </RightContainer>
      </InnerContainer>
      {theme.featureToggles?.showSendMyOffer && (
        <EmailButton
          className="hide-for-large"
          email={props.email}
          onEmailModalSubmit={props.onEmailModalSubmit}
          showEmailModalButton={props.showEmailModalButton}
        />
      )}
    </Container>
  );
};
